import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { adminAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';
import { toast } from 'sonner';
import { Download, CheckCircle, XCircle, Ban } from 'lucide-react';

const AdminMobilitiers = () => {
  const [mobs, setMobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAllMobilitiers();
      setMobs(res.data || []);
    } catch (e) {
      toast.error('Error cargando mobilitiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const verify = async (mob, approve) => {
    const reason = approve ? '' : (window.prompt('Motivo del rechazo:') || '');
    try {
      await adminAPI.verifyMobilitier(mob.id, { verified: approve, reason });
      toast.success(`Mobilitier ${approve ? 'verificado' : 'rechazado'}`);
      load();
    } catch (e) {
      toast.error('Error');
    }
  };

  const toggleBan = async (m) => {
    const isBanned = !!m.banned;
    const reason = isBanned ? '' : (window.prompt('Motivo del ban:') || 'No especificado');
    if (!isBanned && reason === null) return;
    try {
      await adminAPI.banUser(m.id, { banned: !isBanned, reason });
      toast.success(`${!isBanned ? 'Baneado' : 'Desbaneado'}`);
      load();
    } catch (e) {
      toast.error('Error');
    }
  };

  const pending = mobs.filter(m => !m.verified);
  const verified = mobs.filter(m => m.verified);

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      <Card data-testid="pending-mobilitiers-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Mobilitiers Pendientes ({pending.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-gray-500 text-sm">No hay solicitudes pendientes.</p>
          ) : (
            <div className="space-y-3">
              {pending.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-4 bg-orange-50 border-l-4 border-orange-400 rounded" data-testid={`pending-mobilitier-${m.id}`}>
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-sm text-gray-600">{m.email}</p>
                    <p className="text-xs text-gray-500 mt-1">{m.cars_count} autos · {m.bookings_count} reservas</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => verify(m, true)} className="bg-green-600 hover:bg-green-700" data-testid={`verify-mobilitier-btn-${m.id}`}>
                      <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => verify(m, false)} data-testid={`reject-mobilitier-btn-${m.id}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="verified-mobilitiers-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Mobilitiers Verificados ({verified.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-3">Nombre</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Autos</th>
                  <th className="p-3">Reservas</th>
                  <th className="p-3">Stripe</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {verified.map((m) => (
                  <tr key={m.id} className="border-t" data-testid={`mobilitier-row-${m.id}`}>
                    <td className="p-3 font-medium">{m.name}</td>
                    <td className="p-3 text-gray-600">{m.email}</td>
                    <td className="p-3">{m.cars_count}</td>
                    <td className="p-3">{m.bookings_count}</td>
                    <td className="p-3">
                      {m.stripe_account_id ? <Badge className="bg-green-600">Conectado</Badge> : <Badge variant="outline">Pendiente</Badge>}
                    </td>
                    <td className="p-3">
                      {m.banned ? <Badge variant="destructive">Baneado</Badge> : <Badge className="bg-blue-600">Activo</Badge>}
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant={m.banned ? 'default' : 'destructive'} onClick={() => toggleBan(m)} data-testid={`mob-ban-toggle-${m.id}`}>
                        <Ban className="w-3 h-3 mr-1" /> {m.banned ? 'Desbanear' : 'Banear'}
                      </Button>
                    </td>
                  </tr>
                ))}
                {verified.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-gray-500">No hay mobilitiers verificados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMobilitiers;
